// ============================================================================
// src/services/resultsApi.js - FSGU Tournament Results API Service
// ============================================================================

const API_BASE_URL = 'https://api.fsgu.co.za';

// ============================================================================
// RESULTS
// ============================================================================

/**
 * Fetch all tournament results
 * @param {Object} filters - Optional filters
 * @returns {Promise<Array>} Array of result objects
 */
export const fetchResults = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.category && filters.category !== 'all') {
      params.append('category', filters.category);
    }
    
    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    
    if (filters.featured) {
      params.append('featured', 'true');
    }
    
    if (filters.year) {
      params.append('year', filters.year);
    }
    
    if (filters.limit) {
      params.append('limit', filters.limit);
    }
    
    if (filters.offset) {
      params.append('offset', filters.offset);
    }

    const url = `${API_BASE_URL}/get-results.php${params.toString() ? '?' + params.toString() : ''}`;
    console.log('🔍 Fetching results from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'API returned unsuccessful response');
    }
    
    console.log(`✅ Successfully fetched ${data.count} results`);
    return data;
    
  } catch (error) {
    console.error('❌ Error fetching results:', error);
    throw error;
  }
};

/**
 * Get a single result by ID or slug
 * @param {number|string} identifier - Result ID or slug
 * @returns {Promise<Object>} Result object
 */
export const fetchResultById = async (identifier) => {
  try {
    const isNumeric = !isNaN(identifier);
    const param = isNumeric ? `id=${identifier}` : `slug=${identifier}`;
    
    const response = await fetch(`${API_BASE_URL}/get-result.php?${param}`);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Result not found');
    }

    return data.data;
  } catch (error) {
    console.error(`❌ Error fetching result ${identifier}:`, error);
    throw error;
  }
};

/**
 * Create a new result (Admin only)
 * @param {Object} resultData - Result data
 * @returns {Promise<Object>} Created result
 */
export const createResult = async (resultData) => {
  try {
    console.log('📝 Creating new result:', resultData);
    
    const response = await fetch(`${API_BASE_URL}/create-result.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(resultData)
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create result');
    }

    console.log('✅ Result created successfully:', data.data);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error creating result:', error);
    throw error;
  }
};

/**
 * Update an existing result (Admin only)
 * @param {number} resultId - Result ID
 * @param {Object} resultData - Updated result data
 * @returns {Promise<Object>} Updated result
 */
export const updateResult = async (resultId, resultData) => {
  try {
    console.log(`📝 Updating result ${resultId}:`, resultData);
    
    const response = await fetch(`${API_BASE_URL}/update-result.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...resultData,
        id: resultId
      })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to update result');
    }

    console.log('✅ Result updated successfully:', data.data);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error updating result:', error);
    throw error;
  }
};

/**
 * Delete a result (Admin only)
 * @param {number} resultId - Result ID
 * @returns {Promise<Object>} Delete confirmation
 */
export const deleteResult = async (resultId) => {
  try {
    console.log(`🗑️ Deleting result ${resultId}`);
    
    const response = await fetch(`${API_BASE_URL}/delete-result.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: resultId })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to delete result');
    }

    console.log('✅ Result deleted successfully');
    return data;
    
  } catch (error) {
    console.error('❌ Error deleting result:', error);
    throw error;
  }
};

/**
 * Get featured results for homepage
 * @param {number} limit - Number of featured results to fetch
 * @returns {Promise<Array>} Array of featured result objects
 */
export const getFeaturedResults = async (limit = 3) => {
  try {
    const response = await fetchResults({
      featured: true,
      status: 'published',
      limit
    });
    
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching featured results:', error);
    throw error;
  }
};

/**
 * Get recent results
 * @param {number} limit - Number of recent results to fetch
 * @returns {Promise<Array>} Array of recent result objects
 */
export const getRecentResults = async (limit = 10) => {
  try {
    const response = await fetchResults({
      status: 'published',
      limit
    });
    
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching recent results:', error);
    throw error;
  }
};

// Export API base URL
export { API_BASE_URL };